import { BaseSupabaseService } from './BaseSupabaseService';
import { handleServiceError } from '../utils/errorHandler';
import {
  SubscriptionPlan,
  StudentSubscription,
  type TrainingCycle,
} from '../types';
import { mapProjectIdentity } from '../utils/project';
import { pickDisplayStudentSubscription } from '../utils/subscriptionLifecycle';
import type { PaginatedResponse } from '../types/student';
import { SharedTokenService, SHARED_TOKEN_PURPOSES } from './SharedTokenService';
import { requestEdgeFunction } from './edgeFunctionClient';

const resolveTrainingCycle = (value: unknown): TrainingCycle => (
  value === 'bimonthly' ? 'bimonthly' : 'monthly'
);

export interface SubscriptionPlansListFilters {
  searchName?: string;
  filterIsActive?: boolean[];
  billingCycles?: string[];
  loyaltyMonths?: number[];
  projectCodes?: string[];
}

type SubscriptionPlansPaginatedResponse = PaginatedResponse<SubscriptionPlan> & {
  meta?: {
    availableBillingCycles: string[];
    availableLoyaltyMonths: number[];
  };
};

const PAGINATED_SUBSCRIPTION_PLANS_FUNCTION = 'list-paginated-subscription-plans';
const MARKETING_PROJECT_SELECT = 'project:marketing_projects(code, name, text_color, bg_color)';
const SUBSCRIPTION_PLAN_SELECT = `*, ${MARKETING_PROJECT_SELECT}`;
const STUDENT_SUBSCRIPTION_SELECT = `*, ${MARKETING_PROJECT_SELECT}`;

function mapStudentSubscription(row: unknown): StudentSubscription {
  const record = row as StudentSubscription & {
    project?: unknown;
    payment_method?: unknown;
    created_by_portal_user_id?: unknown;
    created_by_name?: unknown;
  };
  const paymentMethod = record.payment_method === 'pix' || record.payment_method === 'credit_card'
    ? record.payment_method
    : null;
  const createdByPortalUserId = Number(record.created_by_portal_user_id);
  const createdByName = typeof record.created_by_name === 'string' && record.created_by_name.trim()
    ? record.created_by_name.trim()
    : null;
  return {
    ...record,
    payment_method: paymentMethod,
    created_by_portal_user_id: Number.isInteger(createdByPortalUserId) && createdByPortalUserId > 0
      ? createdByPortalUserId
      : null,
    created_by_name: createdByName,
    training_cycle: resolveTrainingCycle(record.training_cycle),
    project: mapProjectIdentity(record.project),
  };
}

function mapSubscriptionPlan(row: unknown): SubscriptionPlan {
  const record = row as SubscriptionPlan & { project?: unknown };
  return {
    ...record,
    training_cycle: resolveTrainingCycle(record.training_cycle),
    project: mapProjectIdentity(record.project),
  };
}

class SubscriptionPlanServiceClass extends BaseSupabaseService<SubscriptionPlan> {
  constructor() {
    super('subscription_plans');
  }

  async getActivePlans(): Promise<SubscriptionPlan[]> {
    try {
      const response = await requestEdgeFunction<{ data: unknown[] }>('list-paginated-subscription-plans', {
        method: 'POST',
        body: { action: 'get-active', payload: {} },
      });
      return (response.data ?? []).map(mapSubscriptionPlan);
    } catch (error) {
      handleServiceError(error, 'buscar planos');
      return [];
    }
  }

  async getSelectablePlans(): Promise<SubscriptionPlan[]> {
    try {
      const response = await requestEdgeFunction<{ data: unknown[] }>('list-paginated-subscription-plans', {
        method: 'POST',
        body: { action: 'get-selectable', payload: {} },
      });
      return (response.data ?? []).map(mapSubscriptionPlan);
    } catch (error) {
      handleServiceError(error, 'buscar planos');
      return [];
    }
  }

  async listProjectByPlanId(): Promise<Map<number, NonNullable<SubscriptionPlan['project']>>> {
    const response = await requestEdgeFunction<{ data: Array<{ id?: number; project?: unknown }> }>('list-paginated-subscription-plans', {
      method: 'POST',
      body: { action: 'list-project-map', payload: {} },
    }).catch((error) => {
      handleServiceError(error, 'buscar projetos dos planos');
      throw error;
    });
    const projectByPlanId = new Map<number, NonNullable<SubscriptionPlan['project']>>();
    for (const row of response.data ?? []) {
      if (typeof row.id !== 'number') {
        continue;
      }

      const project = mapProjectIdentity(row.project);
      if (project) {
        projectByPlanId.set(row.id, project);
      }
    }

    return projectByPlanId;
  }

  async findById(id: number | string): Promise<SubscriptionPlan | null> {
    try {
      const response = await requestEdgeFunction<{ data: unknown | null }>('list-paginated-subscription-plans', {
        method: 'POST',
        body: {
          action: 'data-find-by-id',
          payload: { id },
        },
      });
      return response.data ? mapSubscriptionPlan(response.data) : null;
    } catch (error) {
      handleServiceError(error, 'buscar plano');
      return null;
    }
  }

  async listPaginated(
    page: number = 1,
    pageSize: number = 10,
    filters: SubscriptionPlansListFilters = {},
  ): Promise<SubscriptionPlansPaginatedResponse> {
    const result = await requestEdgeFunction<SubscriptionPlansPaginatedResponse>(PAGINATED_SUBSCRIPTION_PLANS_FUNCTION, {
      method: 'GET',
      query: {
        page,
        pageSize,
        searchName: filters.searchName?.trim() || undefined,
        filterIsActive: filters.filterIsActive?.length ? filters.filterIsActive : undefined,
        billingCycles: filters.billingCycles?.length ? filters.billingCycles : undefined,
        loyaltyMonths: filters.loyaltyMonths?.length ? filters.loyaltyMonths : undefined,
        projectCodes: filters.projectCodes?.length ? filters.projectCodes : undefined,
      },
    }).catch((error) => {
      handleServiceError(
        error,
        'listar planos de assinatura',
      );
      throw error;
    });
    result.data = (result.data ?? []).map((row) => ({
      ...row,
      project: mapProjectIdentity(row.project) ?? mapProjectIdentity(row),
    }));

    return result;
  }

  async generateCheckoutLink(planId: number): Promise<string> {
    const data = await SharedTokenService.encode(SHARED_TOKEN_PURPOSES.CHECKOUT_PLAN, { planId });
    return data.token;
  }
}

class StudentSubscriptionServiceClass extends BaseSupabaseService<StudentSubscription> {
  constructor() {
    super('student_subscriptions');
  }

  async findByPaymentAccountId(paymentAccountId: number): Promise<StudentSubscription | null> {
    const results = await this.findAllByPaymentAccountId(paymentAccountId);
    return results[0] || null;
  }

  async findAllByPaymentAccountId(paymentAccountId: number): Promise<StudentSubscription[]> {
    try {
      const response = await requestEdgeFunction<{ data: unknown[] }>('payment-gateway', {
        method: 'POST',
        body: {
          action: 'list-student-subscriptions-by-payment-account',
          payload: { payment_account_id: paymentAccountId },
        },
      });
      return (response.data ?? []).map(mapStudentSubscription);
    } catch (error) {
      handleServiceError(error, 'buscar assinaturas');
      return [];
    }
  }

  /**
   * Resolve the subscriptions covering a given student on a given payment account
   * junction, via the coverage link table (student_subscription_students).
   *
   * On shared/family accounts the canonical subscription lives on the account
   * owner's junction, but is linked to every covered student. This resolves the
   * subscription for both the owner and any additional students who share it.
   */
  async findAllLinkedByStudentJunction(
    studentId: number,
    studentPaymentAccountId: number,
  ): Promise<StudentSubscription[]> {
    try {
      const response = await requestEdgeFunction<{ data: unknown[] }>('payment-gateway', {
        method: 'POST',
        body: {
          action: 'list-linked-student-subscriptions',
          payload: {
            student_id: studentId,
            student_payment_account_id: studentPaymentAccountId,
          },
        },
      });
      return (response.data ?? []).map(mapStudentSubscription);
    } catch (error) {
      handleServiceError(error, 'buscar assinaturas vinculadas');
      return [];
    }
  }

  async findLinkedByStudentJunction(
    studentId: number,
    studentPaymentAccountId: number,
  ): Promise<StudentSubscription | null> {
    const results = await this.findAllLinkedByStudentJunction(studentId, studentPaymentAccountId);
    return pickDisplayStudentSubscription(results);
  }

  async updateTrainingCycle(
    studentSubscriptionId: number,
    trainingCycle: TrainingCycle,
  ): Promise<StudentSubscription> {
    try {
      const response = await requestEdgeFunction<{ data: unknown }>('payment-gateway', {
        method: 'POST',
        body: {
          action: 'update-student-subscription-training-cycle',
          payload: {
            student_subscription_id: studentSubscriptionId,
            training_cycle: trainingCycle,
          },
        },
      });
      return mapStudentSubscription(response.data);
    } catch (error) {
      handleServiceError(error, 'atualizar o ciclo de treino');
      throw error;
    }
  }

}

export const SubscriptionPlanService = new SubscriptionPlanServiceClass();
export const StudentSubscriptionService = new StudentSubscriptionServiceClass();
